import { Card, IconButton, LuIcon, Text } from "@overmap-ai/blocks"
import { type Document, UNTITLED_DOCUMENT_TITLE } from "@overmap-ai/common"
import { selectDocumentById } from "@overmap-ai/core"
import { useNavigate } from "@tanstack/react-router"
import { type NodeViewProps, NodeViewWrapper } from "@tiptap/react"
import { cx } from "class-variance-authority"
import { memo, type MouseEventHandler, useCallback } from "react"

import { DocumentIcon } from "../../../components/Icons/DocumentIcon"
import { LuIconWithWrapper } from "../../../components/Icons/LuIconWithWrapper"
import { modelIconMapping } from "../../../mappings/icons"
import { useAppSelector } from "../../../store"

export const DocumentLinkWrapper = memo((props: NodeViewProps) => {
	const { node, deleteNode, selected, editor } = props
	const documentId = node.attrs.uuid as Document["uuid"]

	const document = useAppSelector(selectDocumentById(documentId))
	const navigate = useNavigate()

	const handleClick = useCallback(() => {
		if (!document) return

		if (document.organization) {
			void navigate({
				from: "/$organizationId",
				to: "/$organizationId/documents/$documentId",
				params: { organizationId: document.organization, documentId: document.uuid },
			})
		} else if (document.project) {
			void navigate({
				from: "/$organizationId/$projectId",
				to: "/$organizationId/$projectId/documents/$documentId",
				params: { projectId: document.project, documentId: document.uuid },
			})
		}
	}, [document, navigate])

	const handleDeleteButtonClicked = useCallback<MouseEventHandler>(
		(e) => {
			e.stopPropagation()
			deleteNode()
		},
		[deleteNode],
	)

	return (
		<NodeViewWrapper className="my-4">
			{document ? (
				<Card
					className={cx("flex cursor-pointer items-center justify-between gap-2", {
						"outline-(--primary-indicator) outline-2 -outline-offset-1":
							selected && editor.isEditable && editor.isFocused,
					})}
					onClick={handleClick}
					size="sm"
				>
					<div className="flex min-w-0 grow items-center gap-2">
						<DocumentIcon documentId={document.uuid} />
						<Text className="truncate font-medium">{document.title || UNTITLED_DOCUMENT_TITLE}</Text>
					</div>

					{editor.isEditable && (
						<IconButton
							aria-label="delete"
							accentColor="base"
							size="sm"
							variant="ghost"
							onClick={handleDeleteButtonClicked}
						>
							<LuIcon icon={modelIconMapping.close} />
						</IconButton>
					)}
				</Card>
			) : (
				<Card
					className={cx("flex cursor-pointer items-center justify-between gap-2", {
						"outline-(--primary-indicator) outline-2 -outline-offset-1": selected && editor.isEditable,
					})}
					size="sm"
				>
					<div className="flex items-center gap-2">
						<LuIconWithWrapper icon={modelIconMapping.document} background />
						<Text accentColor="base">Document not found</Text>
					</div>

					{editor.isEditable && (
						<IconButton
							aria-label="delete"
							accentColor="base"
							size="sm"
							variant="ghost"
							onClick={handleDeleteButtonClicked}
						>
							<LuIcon icon={modelIconMapping.close} />
						</IconButton>
					)}
				</Card>
			)}
		</NodeViewWrapper>
	)
})
DocumentLinkWrapper.displayName = "DocumentLinkWrapper"
